import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGroupMembership } from '@/hooks/useGroupMembership';
import { fetchSeries, fetchUserBets, fetchSeriesPicksForGroup } from '@/lib/supabase-queries';
import { supabase } from '@/integrations/supabase/client';
import { DrillInLayout } from '@/components/DrillInLayout';
import SeriesList from '@/components/SeriesList';
import { PicksRevealSheet, type RevealPick, type PicksRevealOutcome } from '@/components/PicksRevealSheet';
import { Loader2 } from 'lucide-react';

const SeriesDrillIn = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuth();
  const { isMember, loading: membershipLoading } = useGroupMembership(groupId, user?.id);
  const [seriesList, setSeriesList] = useState<any[]>([]);
  const [userBets, setUserBets] = useState<any[]>([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [revealSeriesId, setRevealSeriesId] = useState<string | null>(null);
  const [revealPicks, setRevealPicks] = useState<RevealPick[]>([]);
  const [revealLoading, setRevealLoading] = useState(false);

  useEffect(() => {
    if (!groupId || !user || !isMember) return;
    const load = async () => {
      const [s, bets, membersRes] = await Promise.all([
        fetchSeries(),
        fetchUserBets(user.id, groupId),
        supabase.from('group_members').select('user_id').eq('group_id', groupId),
      ]);
      setSeriesList(s);
      setUserBets(bets);
      setTotalMembers(membersRes.data?.length ?? 0);
      setLoading(false);
    };
    load();
  }, [groupId, user, isMember]);

  const handleRevealSeries = async (seriesId: string) => {
    if (!groupId) return;
    setRevealSeriesId(seriesId);
    setRevealLoading(true);
    setRevealPicks([]);
    try {
      const picks = await fetchSeriesPicksForGroup(groupId, seriesId);
      const mapped: RevealPick[] = picks.map((p) => {
        const match = p.predicted_score.match(/(\d+)-(\d+)/);
        const label = match
          ? `${p.predicted_winner} in ${Number(match[1]) + Number(match[2])}`
          : p.predicted_winner;
        return {
          user_id: p.user_id,
          display_name: p.display_name,
          avatar_id: p.avatar_id,
          label,
          points_earned: p.points_earned,
          is_correct_winner: p.is_correct_winner,
          is_correct_score: p.is_correct_score,
        };
      });
      setRevealPicks(mapped);
    } catch (err) {
      console.warn('fetchSeriesPicksForGroup failed:', err);
    } finally {
      setRevealLoading(false);
    }
  };

  const revealSeries = revealSeriesId ? seriesList.find((s) => s.id === revealSeriesId) : null;

  const revealOutcome: PicksRevealOutcome | undefined = (() => {
    if (!revealSeries) return undefined;
    if (revealSeries.status !== 'completed' || !revealSeries.winner) return undefined;
    const fs: string = revealSeries.final_score ?? '';
    const m = fs.match(/(\d+)-(\d+)/);
    const total = m ? Number(m[1]) + Number(m[2]) : 0;
    const displayScore = m ? `${m[2]}-${m[1]}` : fs;
    return {
      winner: revealSeries.winner,
      finalScore: displayScore,
      winningLabel: total > 0 ? `${revealSeries.winner} in ${total}` : revealSeries.winner,
    };
  })();

  if (!membershipLoading && isMember === false) return <Navigate to="/" replace />;

  return (
    <DrillInLayout title="כל הסדרות" groupId={groupId!}>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      ) : (
        <div className="container mx-auto px-4 py-6">
          <SeriesList seriesList={seriesList} groupId={groupId!} userBets={userBets} onRevealClick={handleRevealSeries} />
        </div>
      )}
      <PicksRevealSheet
        open={revealSeriesId !== null}
        onClose={() => setRevealSeriesId(null)}
        title={revealSeries ? `${revealSeries.team_home} vs ${revealSeries.team_away}` : ''}
        subtitle={revealLoading ? 'טוען...' : 'בחירות חברי הקבוצה'}
        picks={revealPicks}
        currentUserId={user?.id}
        totalMembers={totalMembers}
        outcome={revealOutcome}
      />
    </DrillInLayout>
  );
};

export default SeriesDrillIn;
